import Image from "next/image";
import { SyntheticEvent, useContext, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Flex,
  Spinner,
  Icon,
  Center,
  Input,
  Button,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { BsFillCheckCircleFill, BsXCircleFill } from "react-icons/bs";

// Constants
import {
  SUBSCRIBE_DEFI_STRATEGIES,
  VIEW_DEFI_STRATEGIES,
} from "constants/analytics";

// Services
import { getPDFStrategies } from "services/common";

// Utils
import { validateEmail } from "utils/common";

// Contexts
import { StakeContext } from "providers/StakeProvider";

// Images
import bookLight from "assets/svg/book-light.svg";
import bookDark from "assets/svg/book-dark.svg";

import { Props } from "./Props.type";

const TransactionModal = ({
  isOpen,
  hash,
  error,
  isTxnProcessing,
  closeAlert,
  handleTxView,
}: Props) => {
  const {
    walletInfo: { address },
    connectorName,
    track,
  } = useContext(StakeContext);

  const [email, setEmail] = useState("");
  const [inputError, setInputError] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  const bookIcon = useColorModeValue(bookLight, bookDark);

  const toast = useToast();

  useEffect(() => {
    if (!isOpen) {
      setEmail("");
    }
    if (isOpen && hash && !error && !isTxnProcessing) {
      track(VIEW_DEFI_STRATEGIES);
    }
  }, [isOpen, hash, error, isTxnProcessing]);

  useEffect(() => {
    if (email) {
      const isValidEmail = validateEmail(email);

      if (isValidEmail) {
        setInputError("");
      } else {
        setInputError("Please enter correct email ID");
      }
    }
  }, [email]);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (isSubscribing) {
      return;
    }

    const data = {
      email,
      wallet: connectorName,
      address,
    };

    if (!validateEmail(email)) {
      setInputError("Please enter correct email ID");
      return;
    }

    setIsSubscribing(true);

    const response = await getPDFStrategies(data);

    track(SUBSCRIBE_DEFI_STRATEGIES, {
      ...data,
      success: response.success,
      isAlreadySubscribed: response.isAlreadySubscribed || "N/A",
    });

    if ((response as any).error) {
      toast({
        description: "Failed to subscribe",
        status: "error",
        duration: 2000,
        position: "top",
        isClosable: true,
      });
      setIsSubscribing(false);

      return;
    }

    toast({
      description: response.message,
      status: response.isAlreadySubscribed ? "error" : "success",
      duration: 3000,
      position: "top",
      isClosable: true,
    });
    setIsSubscribing(false);
    closeAlert();
  };

  const renderContent = () => {
    if (error) {
      return (
        <>
          <Icon as={BsXCircleFill} color="#E53E3E" fontSize="5rem" />
          <Text fontSize="14px">Transaction Failed</Text>
          {hash ? (
            <Text
              fontSize="14px"
              color="brand.primary"
              fontFamily="medium"
              cursor="pointer"
              onClick={() => handleTxView()}
            >
              View on Explorer
            </Text>
          ) : (
            <Text fontSize="14px" fontFamily="medium" textAlign="center">
              {error}
            </Text>
          )}
        </>
      );
    } else if (hash && !error) {
      if (isTxnProcessing) {
        return (
          <>
            <Spinner size="xl" color="brand.primary" />
            <Text fontSize="14px">Transaction Submitted.</Text>
            <Text fontSize="14px">Waiting for confirmation. Please Wait!</Text>
            <Text
              fontSize="14px"
              color="brand.primary"
              fontFamily="medium"
              cursor="pointer"
              onClick={() => handleTxView()}
            >
              View on Explorer
            </Text>
          </>
        );
      } else {
        return (
          <>
            <Icon as={BsFillCheckCircleFill} color="#206846" fontSize="5rem" />
            <Text fontSize="14px">Transaction Successful.</Text>
            <Text
              fontSize="14px"
              color="brand.primary"
              fontFamily="medium"
              cursor="pointer"
              onClick={() => handleTxView()}
            >
              View on Explorer
            </Text>
          </>
        );
      }
    } else {
      return (
        <>
          <Spinner size="xl" color="brand.primary" />
          <Text fontSize="14px">Please check your wallet for prompt.</Text>
        </>
      );
    }
  };

  const renderExtra = () => {
    if (hash && !error && !isTxnProcessing) {
      return (
        <Center
          w="100%"
          bg="brand.background"
          p="2rem 1rem"
          gap="1rem"
          borderRadius="8px"
          flexDir="column"
        >
          <Center gap="10px" fontSize={14}>
            <Image src={bookIcon.src} alt="book-icon" width={24} height={24} />
            <Text>Don&apos;t miss your free DeFi strategy guide!</Text>
          </Center>

          <form onSubmit={handleSubmit}>
            <Input
              mt="0.5rem"
              mb="0"
              w="346px"
              bg="brand.backgroundLight"
              type="email"
              required
              placeholder="Email address"
              value={email}
              fontSize="1rem"
              h="48px"
              pl="2rem"
              borderRadius="16px"
              outline="none"
              onChange={(e) => setEmail(e.target.value)}
              _focus={{
                outline: "none",
                border: "none",
              }}
            />
            {inputError && (
              <Text
                fontWeight="400"
                fontSize="14px"
                color="#e53e3e"
                mt="0.5rem"
              >
                {inputError}
              </Text>
            )}
            <Center>
              <Button
                size="sm"
                w="152px"
                type="submit"
                mt="1rem"
                bg="brand.primary"
                color="white"
                h="40px"
              >
                {isSubscribing ? (
                  <Spinner size="md" color="white" />
                ) : (
                  <Text>Subscribe</Text>
                )}
              </Button>
            </Center>
          </form>
        </Center>
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={closeAlert} isCentered>
      <ModalOverlay />
      <ModalContent w="394px" bg="brand.backgroundLight">
        <ModalHeader fontSize="1rem">Transaction</ModalHeader>
        <ModalCloseButton
          _focus={{
            boxShadow: "none",
          }}
        />
        <ModalBody p="0" borderRadius="8px">
          <>
            <Flex
              flexDir="column"
              justifyContent="center"
              alignItems="center"
              padding="1rem"
              gap="1rem"
            >
              {renderContent()}
            </Flex>
            {renderExtra()}
          </>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TransactionModal;
